import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  leadGenAPI,
  linkedinAPI,
  googleMapsAPI,
  HEADCOUNT_TEXT_TO_ID,
  HEADCOUNT_ID_TO_TEXT,
  INDUSTRY_NAME_TO_ID,
} from "../services/api";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WorkIcon from "@mui/icons-material/Work";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

// List of all countries
const countries = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kosovo",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
].sort();

// LinkedIn location IDs mapping
const LINKEDIN_LOCATION_IDS = {
  Mexico: 103323778,
  Argentina: 100446943,
  Bolivia: 104379274,
  Brazil: 106057199,
  Colombia: 100876405,
  Ecuador: 106373116,
  Guyana: 105836293,
  Paraguay: 104065273,
  Peru: 102927786,
  Suriname: 105530931,
  Uruguay: 100867946,
  Venezuela: 101490751,
  Canada: 101174742,
  "United States": 103644278,
  Belize: 105912732,
  "Costa Rica": 101739942,
  "El Salvador": 106522560,
  Guatemala: 100877388,
  Honduras: 101937718,
  Nicaragua: 105517145,
  Panama: 100808673,
  Cuba: 106429766,
  Haiti: 100993490,
  "Dominican Republic": 105057336,
};

// Get country name from location ID (for reverse lookup)
const getCountryNameFromId = (locationId) => {
  const entry = Object.entries(LINKEDIN_LOCATION_IDS).find(
    ([_, id]) => id.toString() === locationId.toString()
  );
  return entry ? entry[0] : "";
};

// Get country names from location IDs array
const getCountryNamesFromIds = (locationIds) => {
  if (!Array.isArray(locationIds)) return [];
  return locationIds
    .map((id) => {
      const entry = Object.entries(LINKEDIN_LOCATION_IDS).find(
        ([_, locationId]) => locationId.toString() === id.toString()
      );
      return entry ? entry[0] : null;
    })
    .filter((name) => name !== null);
};

const companySizeOptions = [
  "Select company size",
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1001+ employees",
];

// Generate unique job ID as UUID
const generateJobId = () => {
  // Use browser's crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback: Generate UUID v4 manually
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Convert company size format from "1-10 employees" to "1,10"
const convertCompanySizeToRange = (size) => {
  if (!size) return null;
  const match = size.match(/(\d+)-(\d+)/);
  if (match) {
    return `${match[1]},${match[2]}`;
  }
  // Handle "1001+ employees"
  if (size.includes("1001+")) {
    return "1001,999999";
  }
  return null;
};

const LeadGeneration = () => {
  const navigate = useNavigate();
  const [showResults, setShowResults] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]); // Array of company IDs
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [notLocationDialogOpen, setNotLocationDialogOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [notCountrySearchTerm, setNotCountrySearchTerm] = useState("");
  const [hasLoadedDecisionMakers, setHasLoadedDecisionMakers] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [availableTitles, setAvailableTitles] = useState([]);
  const [isLoadingDecisionMakers, setIsLoadingDecisionMakers] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedDecisionMaker, setSelectedDecisionMaker] = useState(null);
  const [selectedDecisionMakers, setSelectedDecisionMakers] = useState([]);

  // Hardcoded roles for decision makers
  const availableRoles = [
    "owner",
    "founder",
    "c_suite",
    "partner",
    "vp",
    "head",
    "director",
    "manager",
    "senior",
    "entry",
    "intern",
  ];
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [noOfDecisionMakers, setNoOfDecisionMakers] = useState(1);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [aiMessage, setAiMessage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [totalCompanies, setTotalCompanies] = useState("5");
  const [searchWithGoogleMaps, setSearchWithGoogleMaps] = useState(false);
  const [searchWithLinkedIn, setSearchWithLinkedIn] = useState(false);
  const [wantPhoneNumber, setWantPhoneNumber] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [formData, setFormData] = useState({
    targetCandidates: "5",
    organizationName: "",
    industry: "",
    companySize: [],
    location: [],
    notLocations: [],
    revenueRangeMin: "",
    revenueRangeMax: "",
    additionalKeywords: "saas",
    searchName: "",
  });
  const [linkedinFormData, setLinkedinFormData] = useState({
    search_name: "",
    industry: [],
    location: [],
    number_of_companies: "10",
    headcount_ranges: [],
  });

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleCheckboxChange = (field) => (value) => {
    setFormData((prev) => {
      const currentArray = prev[field] || [];
      const isSelected = currentArray.includes(value);
      return {
        ...prev,
        [field]: isSelected
          ? currentArray.filter((item) => item !== value)
          : [...currentArray, value],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate required fields
    if (!formData.searchName || formData.searchName.trim() === "") {
      toast.error("Please enter a batch name");
      return;
    }

    if (!aiQuery || aiQuery.trim() === "") {
      toast.error("Please enter your search query");
      return;
    }

    if (!totalCompanies || parseInt(totalCompanies) < 1) {
      toast.error("Please enter a valid number of companies");
      return;
    }

    // Validate at least one checkbox is selected
    if (!searchWithGoogleMaps && !searchWithLinkedIn) {
      toast.error(
        "Please select at least one search option (Google Maps or LinkedIn)"
      );
      return;
    }

    setIsLoading(true);
    setErrorMessage(null); // Clear previous errors

    try {
      const batchName = formData.searchName.trim();
      const totalCompaniesNum = parseInt(totalCompanies);

      // Prepare Google Maps payload (AI mode)
      // Add companies count to the end of the query instead of using target_companies key
      const queryWithCompanies = `${aiQuery.trim()} ${totalCompaniesNum} companies`;
      const googleMapsPayload = {
        search_name: batchName,
        job_id: generateJobId(),
        additional_params: {
          with_query: true,
          query: queryWithCompanies,
        },
        phone_number: wantPhoneNumber,
      };

      // Prepare LinkedIn payload
      // For LinkedIn, we'll use a simplified approach with the AI query
      // Note: LinkedIn API might need industry and location, but we're using AI query
      const linkedinJobId = generateJobId();
      const linkedinPayload = {
        search_name: batchName,
        job_id: linkedinJobId,
        number_of_companies: totalCompaniesNum,
        ai_query: aiQuery.trim(),
      };

      // Call APIs based on checkboxes
      let googleMapsSuccess = false;
      let linkedinSuccess = false;
      let googleMapsJobId = null;
      let linkedinJobIdResult = null;
      let apiErrorSet = false; // Track if API error was already set

      if (searchWithGoogleMaps) {
        try {
          googleMapsJobId = googleMapsPayload.job_id;
          const googleMapsResponse = await googleMapsAPI.search(
            googleMapsPayload
          );
          console.log("Google Maps Search API response:", googleMapsResponse);

          if (googleMapsResponse.status === "success") {
            googleMapsSuccess = true;
            // Store search response in localStorage for CompanyResults page
            localStorage.setItem(
              `google_maps_search_${googleMapsJobId}`,
              JSON.stringify(googleMapsResponse)
            );
            toast.success("Google Maps search initiated successfully");
          } else {
            // Handle error response from API
            const errorMsg =
              googleMapsResponse.message ||
              googleMapsResponse.detail ||
              "Google Maps search failed";
            setErrorMessage(errorMsg);
            apiErrorSet = true;
            toast.error(errorMsg, { duration: 6000 });
          }
        } catch (error) {
          console.error("Error in Google Maps search:", error);
          console.log("Error response data:", error.response?.data);

          // Extract error message from response - prioritize detail field
          let errorMessage = "Google Maps search failed. ";
          if (error.response?.data) {
            // First check for detail field (common in FastAPI/backend errors)
            if (error.response.data.detail) {
              errorMessage = error.response.data.detail;
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
            } else if (error.response.data.error) {
              errorMessage = error.response.data.error;
            } else {
              errorMessage += `Server error: ${error.response.status}`;
            }
          } else if (error.request) {
            errorMessage += "Network error - Please check your connection.";
          } else {
            errorMessage += error.message || "Unknown error occurred.";
          }

          setErrorMessage(errorMessage);
          apiErrorSet = true;
          toast.error(errorMessage, { duration: 6000 });
        }
      }

      if (searchWithLinkedIn) {
        try {
          linkedinJobIdResult = linkedinJobId;

          // Use the AI query directly as the query parameter
          // The query should already be in the format: "Find X companies in location Y with Z employees"
          const query = aiQuery.trim();

          // Call LinkedIn API with the new payload format
          const linkedinResponse = await linkedinAPI.search(
            batchName,
            query,
            linkedinJobId,
            totalCompaniesNum,
            wantPhoneNumber
          );

          console.log("LinkedIn Search API response:", linkedinResponse);

          if (linkedinResponse.success) {
            linkedinSuccess = true;
            toast.success("LinkedIn search initiated successfully");
          } else {
            const errorMsg =
              linkedinResponse.message ||
              linkedinResponse.detail ||
              "LinkedIn search failed";
            setErrorMessage(errorMsg);
            apiErrorSet = true;
            toast.error(errorMsg, { duration: 6000 });
          }
        } catch (error) {
          console.error("Error in LinkedIn search:", error);
          console.log("Error response data:", error.response?.data);

          // Extract error message from response - prioritize detail field
          let errorMsg = "LinkedIn search failed. ";
          if (error.response?.data) {
            // First check for detail field (common in FastAPI/backend errors)
            if (error.response.data.detail) {
              errorMsg = error.response.data.detail;
            } else if (error.response.data.message) {
              errorMsg = error.response.data.message;
            } else if (typeof error.response.data === "string") {
              errorMsg = error.response.data;
            } else {
              errorMsg += `Server error: ${error.response.status}`;
            }
          } else if (error.request) {
            errorMsg += "Network error - Please check your connection.";
          } else {
            errorMsg += error.message || "Unknown error occurred.";
          }
          setErrorMessage(errorMsg);
          apiErrorSet = true;
          toast.error(errorMsg, { duration: 6000 });
        }
      }

      // Save search to history
      const searchHistoryItem = {
        id: Date.now().toString(),
        batchName: batchName,
        aiQuery: aiQuery,
        totalCompanies: totalCompaniesNum,
        searchWithGoogleMaps: searchWithGoogleMaps,
        searchWithLinkedIn: searchWithLinkedIn,
        googleMapsJobId: googleMapsJobId,
        linkedinJobId: linkedinJobIdResult,
        timestamp: new Date().toISOString(),
      };

      const existingHistory = localStorage.getItem("lead_search_history");
      let history = [];
      if (existingHistory) {
        try {
          history = JSON.parse(existingHistory);
        } catch (error) {
          console.error("Error parsing search history:", error);
        }
      }

      history.unshift(searchHistoryItem);
      if (history.length > 100) {
        history = history.slice(0, 100);
      }
      localStorage.setItem("lead_search_history", JSON.stringify(history));

      // Navigate based on which search succeeded
      if (searchWithGoogleMaps && googleMapsSuccess && !searchWithLinkedIn) {
        // Only Google Maps
        setErrorMessage(null); // Clear errors on success
        navigate(
          `/company-results?job_id=${googleMapsJobId}&source=googlemaps&from=candidate-sourcing`
        );
      } else if (
        searchWithLinkedIn &&
        linkedinSuccess &&
        !searchWithGoogleMaps
      ) {
        // Only LinkedIn
        setErrorMessage(null); // Clear errors on success
        navigate(
          `/company-results?job_id=${linkedinJobIdResult}&source=linkedin&from=candidate-sourcing`
        );
      } else if (googleMapsSuccess || linkedinSuccess) {
        // Both or one succeeded - navigate to the first successful one
        setErrorMessage(null); // Clear errors on success
        if (googleMapsSuccess) {
          navigate(
            `/company-results?job_id=${googleMapsJobId}&source=googlemaps&from=candidate-sourcing`
          );
        } else if (linkedinSuccess) {
          navigate(
            `/company-results?job_id=${linkedinJobIdResult}&source=linkedin&from=candidate-sourcing`
          );
        }
      } else {
        // Both failed - don't override error message if already set from API
        // The error message should already be set from the catch blocks above
        if (!apiErrorSet) {
          const allFailedMsg = "All searches failed. Please try again.";
          setErrorMessage(allFailedMsg);
          toast.error(allFailedMsg);
        }
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      let errorMessage = "Failed to submit search. ";
      if (error.response) {
        errorMessage += `Server error: ${error.response.status}`;
        if (error.response.data?.message) {
          errorMessage += ` - ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage += "Network error - Please check your connection.";
      } else {
        errorMessage += error.message || "Unknown error occurred.";
      }
      toast.error(errorMessage, { duration: 8000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setShowResults(false);
  };

  const handleCompanyToggle = (company) => {
    // Use a unique identifier - prefer place_id, then company_apollo_id, then id, then index
    const companyId =
      company.place_id ||
      company.company_apollo_id ||
      company.id ||
      `company_${companies.indexOf(company)}`;

    setSelectedCompanies((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
    // Reset decision makers when selection changes
    setHasLoadedDecisionMakers(false);
    setDecisionMakers([]);
    setSelectedTitles([]);
    setAvailableTitles([]);
    setSelectedDecisionMakers([]);
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles((prev) => {
      if (prev.includes(role)) {
        return prev.filter((r) => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleFindDecisionMakers = async () => {
    if (selectedCompanies.length === 0) {
      toast.error("Please select at least one company");
      return;
    }
    if (selectedRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }
    if (
      !noOfDecisionMakers ||
      noOfDecisionMakers < 1 ||
      noOfDecisionMakers > 20
    ) {
      toast.error("Please enter a valid number of decision makers (1-20)");
      return;
    }

    setIsLoadingDecisionMakers(true);
    try {
      // Get selected company objects from companies array
      const selectedCompanyObjects = companies.filter((company) => {
        const companyId =
          company.place_id ||
          company.company_apollo_id ||
          company.id ||
          `company_${companies.indexOf(company)}`;
        return selectedCompanies.includes(companyId);
      });

      // Extract company names
      const companiesNameList = selectedCompanyObjects.map(
        (company) => company.name || "Unknown Company"
      );

      // Extract website URLs from selected companies
      // For LinkedIn companies, website URLs might be null/empty, which is okay
      const websiteUrls = selectedCompanyObjects
        .map((company) => {
          const url =
            company.website ||
            company.website_url ||
            company.regular_company_url ||
            company.company_url;
          // Only include valid URLs
          if (
            url &&
            (url.startsWith("http://") || url.startsWith("https://"))
          ) {
            return url;
          } else if (url) {
            // Add https:// if URL doesn't start with http
            return `https://${url}`;
          }
          return null;
        })
        .filter((url) => url !== null); // Remove null/undefined values

      // Determine if this is Google Maps or LinkedIn based on company data
      // Google Maps companies have place_id, LinkedIn companies have company_apollo_id
      const isGoogleMaps = selectedCompanyObjects.some(
        (company) => company.place_id
      );

      // Prepare Apollo people search payload
      // For LinkedIn, website_urls can be empty array if companies don't have website URLs
      const apolloPayload = {
        google_maps_agent_call: isGoogleMaps, // true for Google Maps, false for LinkedIn
        no_of_decision_makers: parseInt(noOfDecisionMakers),
        companies_name_list: companiesNameList,
        list_of_roles: selectedRoles,
        website_urls: websiteUrls, // Can be empty array for LinkedIn companies
      };

      console.log("Apollo people search payload:", apolloPayload);

      // Search decision makers using Apollo API
      const response = await leadGenAPI.searchPeople(apolloPayload);

      console.log("Apollo API response:", response);

      // Extract enriched_data from Apollo API response
      let decisionMakersList = [];
      if (response.enriched_data && Array.isArray(response.enriched_data)) {
        // Apollo API returns enriched_data array with detailed information
        decisionMakersList = response.enriched_data;
      } else if (
        response.search_response?.people &&
        Array.isArray(response.search_response.people)
      ) {
        // Fallback to search_response.people if enriched_data is not available
        decisionMakersList = response.search_response.people;
      } else if (Array.isArray(response)) {
        decisionMakersList = response;
      } else if (response.data && Array.isArray(response.data)) {
        decisionMakersList = response.data;
      } else if (
        response.decision_makers &&
        Array.isArray(response.decision_makers)
      ) {
        decisionMakersList = response.decision_makers;
      } else if (response.people && Array.isArray(response.people)) {
        decisionMakersList = response.people;
      } else {
        // If response structure is different, try to extract array from response
        console.warn("Unexpected response structure:", response);
        decisionMakersList = [];
      }

      // Map enriched_data to match expected format
      const mappedDecisionMakers = decisionMakersList.map((dm) => {
        // Extract organization name and id from nested organization object
        const organizationName =
          dm.organization?.name || dm.organization_name || "";
        const organizationId = dm.organization?.id || dm.organization_id || "";

        // Build full name from first_name and last_name if name is not available
        const fullName =
          dm.name ||
          `${dm.first_name || ""} ${dm.last_name || ""}`.trim() ||
          "Unknown";

        // Extract location from formatted_address or build from city, state, country
        let location = dm.formatted_address || "";
        if (!location && (dm.city || dm.state || dm.country)) {
          const locationParts = [dm.city, dm.state, dm.country].filter(Boolean);
          location = locationParts.join(", ");
        }

        return {
          person_id: dm.id || dm.person_id,
          first_name: dm.first_name || "",
          last_name: dm.last_name || "",
          full_name: fullName,
          title: dm.title || dm.headline || "",
          email: dm.email || "",
          linkedin_url: dm.linkedin_url || "",
          photo_url: dm.photo_url || "",
          organization_id: organizationId,
          organization_name: organizationName,
          domain: dm.organization?.primary_domain || "",
          phone_number: dm.phone || dm.phone_number || "",
          location: location,
          city: dm.city || "",
          state: dm.state || "",
          country: dm.country || "",
          postal_code: dm.postal_code || "",
          headline: dm.headline || "",
          email_status: dm.email_status || "",
          seniority: dm.seniority || "",
          // Keep original data for reference
          ...dm,
        };
      });

      setDecisionMakers(mappedDecisionMakers);
      setHasLoadedDecisionMakers(true);

      // Extract unique titles from decision makers
      const titles = [
        ...new Set(mappedDecisionMakers.map((dm) => dm.title).filter(Boolean)),
      ];
      setAvailableTitles(titles);

      toast.success(`Found ${mappedDecisionMakers.length} decision makers`);
    } catch (error) {
      console.error("Error fetching decision makers:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load decision makers";
      toast.error(errorMessage);
      setDecisionMakers([]);
    } finally {
      setIsLoadingDecisionMakers(false);
    }
  };

  const handleCompanyNameClick = (e, company) => {
    e.stopPropagation(); // Prevent card click
    if (company.website || company.website_url) {
      const url = company.website || company.website_url;
      window.open(url.startsWith("http") ? url : `https://${url}`, "_blank");
    }
  };

  const handleLocationIconClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setLocationDialogOpen(true);
  };

  const handleCountrySelect = (country) => {
    handleCheckboxChange("location")(country);
  };

  const handleNotCountrySelect = (country) => {
    handleCheckboxChange("notLocations")(country);
  };

  const filteredCountries = countries.filter((country) =>
    country.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  // Filter decision makers based on selected titles
  const filteredDecisionMakers =
    decisionMakers.length > 0 && selectedTitles.length > 0
      ? decisionMakers.filter((dm) => {
          return selectedTitles.includes(dm.title);
        })
      : decisionMakers;

  // Handle title filter toggle
  const handleTitleToggle = (title) => {
    setSelectedTitles((prev) => {
      if (prev.includes(title)) {
        return prev.filter((t) => t !== title);
      } else {
        return [...prev, title];
      }
    });
  };

  // Handle decision maker selection toggle
  const handleDecisionMakerToggle = (dm) => {
    setSelectedDecisionMakers((prev) => {
      const dmId = dm.person_id || dm.id;
      const isSelected = prev.some((d) => (d.person_id || d.id) === dmId);
      if (isSelected) {
        return prev.filter((d) => (d.person_id || d.id) !== dmId);
      } else {
        return [...prev, dm];
      }
    });
  };

  // Check if decision maker is selected
  const isDecisionMakerSelected = (dm) => {
    const dmId = dm.person_id || dm.id;
    return selectedDecisionMakers.some((d) => (d.person_id || d.id) === dmId);
  };

  // Message modal handlers
  const handleGenerateMessage = async () => {
    if (!userPrompt.trim()) {
      toast.error("Please enter a message prompt");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await linkedinAPI.generateMessage(userPrompt);
      setAiMessage(response.message || response.text || "");
      toast.success("Message generated successfully!");
    } catch (error) {
      console.error("Error generating message:", error);
      toast.error("Failed to generate message. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateMessage = async () => {
    if (!userPrompt.trim()) {
      toast.error("Please enter a message prompt");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await linkedinAPI.generateMessage(userPrompt);
      setAiMessage(response.message || response.text || "");
      toast.success("Message regenerated successfully!");
    } catch (error) {
      console.error("Error regenerating message:", error);
      toast.error("Failed to regenerate message. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendMessages = async () => {
    if (!aiMessage.trim()) {
      toast.error("Please generate a message first");
      return;
    }

    if (selectedDecisionMakers.length === 0) {
      toast.error("No decision makers selected to send messages to");
      return;
    }

    setIsSending(true);
    const decisionMakersWithLinkedIn = selectedDecisionMakers.filter(
      (dm) => dm.linkedin_url
    );

    if (decisionMakersWithLinkedIn.length === 0) {
      toast.error("No decision makers with LinkedIn profiles found");
      setIsSending(false);
      return;
    }

    try {
      // Collect all LinkedIn URLs
      const linkedInUrls = decisionMakersWithLinkedIn.map(
        (dm) => dm.linkedin_url
      );

      // Send messages to all decision makers with LinkedIn profiles in one API call
      await linkedinAPI.sendMessage(linkedInUrls, aiMessage);

      const successCount = linkedInUrls.length;

      toast.success(`Successfully sent ${successCount} message(s)!`);
      setMessageModalOpen(false);
      setUserPrompt("");
      setAiMessage("");
      setSelectedDecisionMakers([]);
    } catch (error) {
      console.error("Error sending messages:", error);
      toast.error("An error occurred while sending messages");
    } finally {
      setIsSending(false);
    }
  };

  // Results View - This is now handled by CompanyResults page
  // Keeping this for backward compatibility but it should not be reached
  if (showResults) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: "100%",
          backgroundColor: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Scrollable Container - Header and Content together */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            overflowX: "hidden",
            minHeight: 0,
            paddingBottom: { 
              xs: "calc(60px + env(safe-area-inset-bottom, 0px))", 
              sm: "calc(40px + env(safe-area-inset-bottom, 0px))", 
              md: "0px" 
            },
            WebkitOverflowScrolling: "touch",
            marginBottom: { xs: "0px", md: "0px" },
          }}
        >
          {/* Title Section */}
          <Box sx={{ padding: "24px 24px 0 24px" }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Find Companies & Decision Makers
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Search for companies and extract decision makers for your outreach
              campaign
            </Typography>
          </Box>

          {/* Two Panel Layout */}
          <Box
            sx={{
              padding: { xs: "16px", sm: "24px" },
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Left Panel: Companies Found */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: "16px", sm: "18px", md: "20px" }
                    }}
                  >
                    Companies Found ({companies.length})
                  </Typography>
                  <Link
                    component="button"
                    onClick={handleBackToSearch}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: "16px" }} />
                    <Typography variant="body2">Back to Search</Typography>
                  </Link>
                </Box>

                {/* Company Cards */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  {isLoading ? (
                    <Typography
                      sx={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#6B7280",
                      }}
                    >
                      Loading companies...
                    </Typography>
                  ) : companies.length > 0 ? (
                    companies.map((company, index) => (
                      <Card
                        key={company.company_apollo_id || index}
                        onClick={() => handleCompanyToggle(company)}
                        sx={{
                          backgroundColor: "#FFFFFF",
                          borderRadius: "12px",
                          padding: "12px 16px",
                          cursor: "pointer",
                          border: selectedCompanies.includes(
                            company.place_id ||
                              company.company_apollo_id ||
                              company.id ||
                              `company_${companies.indexOf(company)}`
                          )
                            ? "2px solid #3B82F6"
                            : "1px solid #E5E7EB",
                          boxShadow: selectedCompanies.includes(
                            company.place_id ||
                              company.company_apollo_id ||
                              company.id ||
                              `company_${companies.indexOf(company)}`
                          )
                            ? "0 4px 6px rgba(0, 0, 0, 0.1)"
                            : "0 1px 3px rgba(0, 0, 0, 0.1)",
                          transition: "all 0.2s",
                          "&:hover": {
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          },
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                          }}
                        >
                          <Checkbox
                            checked={selectedCompanies.includes(
                              company.place_id ||
                                company.company_apollo_id ||
                                company.id ||
                                `company_${companies.indexOf(company)}`
                            )}
                            onChange={() => handleCompanyToggle(company)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              padding: 0,
                              marginTop: "2px",
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Blue Icon in Top Right */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: "12px",
                                right: "16px",
                              }}
                            >
                              <CorporateFareIcon
                                sx={{ color: "#3B82F6", fontSize: "20px" }}
                              />
                            </Box>

                            {/* Company Name - Large, Bold, Clickable */}
                            <Typography
                              variant="h6"
                              onClick={(e) =>
                                handleCompanyNameClick(e, company)
                              }
                              sx={{
                                fontWeight: 700,
                                fontSize: "18px",
                                mb: 1.5,
                                paddingRight: "40px",
                                color: "#101828",
                                cursor:
                                  company.website || company.website_url
                                    ? "pointer"
                                    : "default",
                                "&:hover": {
                                  color:
                                    company.website || company.website_url
                                      ? "#3B82F6"
                                      : "#101828",
                                  textDecoration:
                                    company.website || company.website_url
                                      ? "underline"
                                      : "none",
                                },
                                transition: "color 0.2s",
                              }}
                            >
                              {company.name}
                            </Typography>

                            {/* Business Type */}
                            {company.business_type && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 1,
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                Type: {company.business_type}
                              </Typography>
                            )}

                            {/* Rating */}
                            {company.rating && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#6B7280",
                                    fontSize: "14px",
                                    mr: 1,
                                  }}
                                >
                                  Rating: {company.rating}
                                </Typography>
                                {company.review_count && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#9CA3AF",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ({company.review_count} reviews)
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Phone Number */}
                            {company.phone_number && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 1,
                                  fontSize: "14px",
                                }}
                              >
                                Phone: {company.phone_number}
                              </Typography>
                            )}

                            {/* Full Address */}
                            {company.full_address && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 0,
                                  fontSize: "14px",
                                  lineHeight: "20px",
                                }}
                              >
                                Address: {company.full_address}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    ))
                  ) : (
                    <Typography
                      sx={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#6B7280",
                      }}
                    >
                      No companies found
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Right Panel: Decision Makers */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2.5,
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: "16px", sm: "18px", md: "20px" }
                    }}
                  >
                    Decision Makers
                  </Typography>
                  {hasLoadedDecisionMakers &&
                    selectedDecisionMakers.length > 0 && (
                      <Button
                        variant="contained"
                        startIcon={<LinkedInIcon />}
                        onClick={() => setMessageModalOpen(true)}
                        sx={{
                          backgroundColor: "#3B82F6",
                          "&:hover": {
                            backgroundColor: "#2563EB",
                          },
                          textTransform: "none",
                          borderRadius: "8px",
                        }}
                      >
                        Send Message ({selectedDecisionMakers.length})
                      </Button>
                    )}
                </Box>

                {/* Role Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Select Roles
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {availableRoles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        onClick={() => handleRoleToggle(role)}
                        clickable
                        sx={{
                          backgroundColor: selectedRoles.includes(role)
                            ? "#3B82F6"
                            : "#FFFFFF",
                          color: selectedRoles.includes(role)
                            ? "#FFFFFF"
                            : "#101828",
                          border: "1px solid #D1D5DB",
                          height: "32px",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: selectedRoles.includes(role)
                              ? "#2563EB"
                              : "#F9FAFB",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Number of Decision Makers Input */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Number of Decision Makers (Max: 20)
                  </Typography>
                  <TextField
                    type="number"
                    fullWidth
                    value={noOfDecisionMakers}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      if (value >= 1 && value <= 20) {
                        setNoOfDecisionMakers(value);
                      } else if (value > 20) {
                        setNoOfDecisionMakers(20);
                        toast.error("Maximum 20 decision makers allowed");
                      } else if (value < 1) {
                        setNoOfDecisionMakers(1);
                      }
                    }}
                    inputProps={{
                      min: 1,
                      max: 20,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#E5E7EB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      },
                    }}
                  />
                </Box>

                {/* Find Decision Makers Button */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleFindDecisionMakers}
                  disabled={
                    selectedCompanies.length === 0 ||
                    selectedRoles.length === 0 ||
                    !noOfDecisionMakers ||
                    noOfDecisionMakers < 1 ||
                    noOfDecisionMakers > 20
                  }
                  sx={{
                    mb: 3,
                    backgroundColor: "#3B82F6",
                    "&:hover": {
                      backgroundColor: "#2563EB",
                    },
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "12px",
                    fontWeight: 600,
                  }}
                >
                  Find Decision Makers
                </Button>

                {/* Title Filter Chips - Show when decision makers are loaded */}
                {availableTitles.length > 0 && (
                  <Box sx={{ marginBottom: "20px" }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Filter by Title
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      {availableTitles.map((title) => (
                        <Chip
                          key={title}
                          label={title}
                          onClick={() => handleTitleToggle(title)}
                          clickable
                          sx={{
                            backgroundColor: selectedTitles.includes(title)
                              ? "primary.main"
                              : "#FFFFFF",
                            color: selectedTitles.includes(title)
                              ? "#FFFFFF"
                              : "text.primary",
                            border: "1px solid #D1D5DB",
                            height: "32px",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: selectedTitles.includes(title)
                                ? "primary.dark"
                                : "#F9FAFB",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Loading State */}
                {isLoadingDecisionMakers && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "40px",
                      minHeight: "300px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center" }}
                    >
                      Loading decision makers...
                    </Typography>
                  </Box>
                )}

                {/* Decision Maker Cards - Show when decision makers are loaded */}
                {!isLoadingDecisionMakers && hasLoadedDecisionMakers && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      marginBottom: "20px",
                      maxHeight: "800px",
                      overflowY: "auto",
                    }}
                  >
                    {filteredDecisionMakers.length > 0 ? (
                      filteredDecisionMakers.map((dm, index) => (
                        <Card
                          key={dm.person_id || index}
                          sx={{
                            backgroundColor: isDecisionMakerSelected(dm)
                              ? "#EFF6FF"
                              : "#FFFFFF",
                            borderRadius: "12px",
                            padding: "16px",
                            boxShadow: isDecisionMakerSelected(dm)
                              ? "0 2px 4px rgba(59, 130, 246, 0.2)"
                              : "0 1px 3px rgba(0, 0, 0, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            border: isDecisionMakerSelected(dm)
                              ? "2px solid #3B82F6"
                              : "1px solid transparent",
                            "&:hover": {
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                              gap: "16px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDecisionMakerToggle(dm);
                            }}
                          >
                            <Checkbox
                              checked={isDecisionMakerSelected(dm)}
                              onChange={() => handleDecisionMakerToggle(dm)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: "#3B82F6",
                                "&.Mui-checked": {
                                  color: "#3B82F6",
                                },
                              }}
                            />
                            {dm.photo_url ? (
                              <Box
                                component="img"
                                src={dm.photo_url}
                                alt={dm.full_name}
                                sx={{
                                  width: "48px",
                                  height: "48px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <PersonIcon
                                sx={{
                                  fontSize: "48px",
                                  color: "#D1D5DB",
                                }}
                              />
                            )}
                            <Box
                              sx={{ flex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDecisionMaker(dm);
                                setProfileModalOpen(true);
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, mb: 0.5 }}
                              >
                                {dm.full_name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "primary.main", mb: 0.5 }}
                              >
                                {dm.title}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <EmailIcon
                                  sx={{
                                    fontSize: "16px",
                                    color: "#6B7280",
                                  }}
                                />
                                <Typography variant="body2">
                                  {dm.email || "No email"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", gap: "8px" }}>
                            {dm.linkedin_url && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(dm.linkedin_url, "_blank");
                                }}
                                sx={{ color: "#0A66C2" }}
                              >
                                <LinkedInIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Card>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", p: 2.5 }}
                      >
                        {selectedCompanies.length === 0
                          ? "Select companies and roles to find decision makers"
                          : selectedRoles.length === 0
                          ? "Select roles to find decision makers"
                          : selectedTitles.length === 0
                          ? "No titles selected. Please select titles to filter decision makers."
                          : "No decision makers found for the selected titles."}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Empty State - Show when no companies selected or decision makers not loaded */}
                {!isLoadingDecisionMakers && !hasLoadedDecisionMakers && (
                  <Card
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "12px",
                      padding: "40px",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      marginBottom: "20px",
                      minHeight: "400px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <PersonIcon
                      sx={{
                        fontSize: "80px",
                        color: "#D1D5DB",
                        marginBottom: "24px",
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      {selectedCompanies.length > 0
                        ? `${selectedCompanies.length} company(ies) selected`
                        : "No companies selected"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedCompanies.length > 0
                        ? "Select roles and click 'Find Decision Makers' to view decision makers"
                        : "Select companies from the list to find decision makers"}
                    </Typography>
                  </Card>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Profile Details Modal */}
        <Dialog
          open={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: "672px",
              maxWidth: "90vw",
              borderRadius: "12px",
              overflow: "hidden",
            },
          }}
        >
          {/* Header Container */}
          <Box
            sx={{
              width: "672px",
              height: "56px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: "24px",
              paddingLeft: "24px",
              paddingTop: "16px",
              paddingBottom: "16px",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "24px",
                color: "#101828",
              }}
            >
              Profile Details
            </Typography>
            <IconButton
              onClick={() => setProfileModalOpen(false)}
              sx={{
                width: "24px",
                height: "24px",
                padding: 0,
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <img
                src="/Button.svg"
                alt="Close"
                style={{ width: "24px", height: "24px" }}
              />
            </IconButton>
          </Box>

          {/* Main Content Container */}
          <Box
            sx={{
              width: "672px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            {selectedDecisionMaker && (
              <>
                {/* Profile Header Container */}
                <Box
                  sx={{
                    width: "624px",
                    height: "87px",
                    display: "flex",
                    gap: "16px",
                  }}
                >
                  {/* Avatar */}
                  {selectedDecisionMaker.photo_url ? (
                    <Box
                      component="img"
                      src={selectedDecisionMaker.photo_url}
                      alt={selectedDecisionMaker.full_name}
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: "80px",
                        height: "80px",
                        bgcolor: "#D1D5DB",
                      }}
                    >
                      {selectedDecisionMaker.full_name?.charAt(0) || "U"}
                    </Avatar>
                  )}

                  {/* Name, Position, Company Container */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0 }}
                  >
                    {/* Name */}
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "18px",
                        lineHeight: "27px",
                        letterSpacing: "0px",
                        color: "#101828",
                        width: "528px",
                        height: "27px",
                      }}
                    >
                      {selectedDecisionMaker.full_name}
                    </Typography>

                    {/* Position */}
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0px",
                        color: "#155DFC",
                        width: "528px",
                        height: "24px",
                      }}
                    >
                      {selectedDecisionMaker.title}
                    </Typography>

                    {/* Company Name */}
                    {selectedDecisionMaker.organization_name && (
                      <Typography
                        sx={{
                          fontFamily: "Arial",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0px",
                          color: "#4A5565",
                          width: "528px",
                          height: "24px",
                        }}
                      >
                        {selectedDecisionMaker.organization_name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Contact Information Section */}
                <Box
                  sx={{
                    width: "624px",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "10px",
                  }}
                >
                  {/* Heading */}
                  <Typography
                    sx={{
                      fontFamily: "Arial",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "24px",
                      letterSpacing: "0px",
                      color: "#101828",
                    }}
                  >
                    Contact Information
                  </Typography>

                  {/* Contact Info Container */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {/* Email */}
                    {selectedDecisionMaker.email && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <img
                          src="/IconEmail.svg"
                          alt="Email"
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0px",
                            color: "#364153",
                          }}
                        >
                          {selectedDecisionMaker.email}
                        </Typography>
                      </Box>
                    )}

                    {/* Phone */}
                    {selectedDecisionMaker.phone_number && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <img
                          src="/IconPhone.svg"
                          alt="Phone"
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0px",
                            color: "#364153",
                          }}
                        >
                          {selectedDecisionMaker.phone_number}
                        </Typography>
                      </Box>
                    )}

                    {/* Location */}
                    {selectedDecisionMaker.location && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <img
                          src="/IconLocation.svg"
                          alt="Location"
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0px",
                            color: "#364153",
                          }}
                        >
                          {selectedDecisionMaker.location}
                        </Typography>
                      </Box>
                    )}

                    {/* LinkedIn */}
                    {selectedDecisionMaker.linkedin_url && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <img
                          src="/IconLinkedIn.svg"
                          alt="LinkedIn"
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Link
                          href={selectedDecisionMaker.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0px",
                            color: "#155DFC",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {selectedDecisionMaker.linkedin_url}
                        </Link>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Experience Section */}
                <Box
                  sx={{
                    width: "624px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {/* Heading with Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <img
                      src="/IconExp.svg"
                      alt="Experience"
                      style={{ width: "16px", height: "16px" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0px",
                        color: "#101828",
                      }}
                    >
                      Experience
                    </Typography>
                  </Box>

                  {/* Experience Text */}
                  <Typography
                    sx={{
                      fontFamily: "Arial",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "24px",
                      letterSpacing: "0px",
                      color: "#364153",
                    }}
                  >
                    {(() => {
                      // Try multiple field names for experience
                      const experience =
                        selectedDecisionMaker.experience ||
                        selectedDecisionMaker.work_experience ||
                        selectedDecisionMaker.experience_summary ||
                        selectedDecisionMaker.years_of_experience ||
                        selectedDecisionMaker.experience_years ||
                        selectedDecisionMaker.total_experience ||
                        (selectedDecisionMaker.experience_years
                          ? `${selectedDecisionMaker.experience_years} years of experience`
                          : null);

                      return (
                        experience || "No experience information available"
                      );
                    })()}
                  </Typography>
                </Box>

                {/* Skills Section */}
                <Box
                  sx={{
                    width: "624px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {/* Heading with Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <img
                      src="/IconSkills.svg"
                      alt="Skills"
                      style={{ width: "16px", height: "16px" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0px",
                        color: "#101828",
                      }}
                    >
                      Skills
                    </Typography>
                  </Box>

                  {/* Skills Container */}
                  <Box
                    sx={{
                      width: "624px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                    }}
                  >
                    {(() => {
                      // Try multiple field names for skills
                      const skills =
                        selectedDecisionMaker.skills ||
                        selectedDecisionMaker.skill_list ||
                        selectedDecisionMaker.competencies ||
                        selectedDecisionMaker.expertise ||
                        selectedDecisionMaker.skills_list ||
                        selectedDecisionMaker.skills_array;

                      if (!skills) {
                        return (
                          <Typography
                            sx={{
                              fontFamily: "Arial",
                              fontWeight: 400,
                              fontSize: "16px",
                              lineHeight: "24px",
                              color: "#9CA3AF",
                            }}
                          >
                            No skills listed
                          </Typography>
                        );
                      }

                      // Handle array of skills
                      if (Array.isArray(skills)) {
                        if (skills.length > 0) {
                          return skills.map((skill, index) => (
                            <Chip
                              key={index}
                              label={
                                typeof skill === "string"
                                  ? skill
                                  : skill.name || skill.title || String(skill)
                              }
                              sx={{
                                backgroundColor: "#EFF6FF",
                                color: "#155DFC",
                                fontFamily: "Arial",
                                fontWeight: 400,
                                fontSize: "14px",
                                height: "32px",
                                borderRadius: "6px",
                                "& .MuiChip-label": {
                                  padding: "0 12px",
                                },
                              }}
                            />
                          ));
                        } else {
                          return (
                            <Typography
                              sx={{
                                fontFamily: "Arial",
                                fontWeight: 400,
                                fontSize: "16px",
                                lineHeight: "24px",
                                color: "#9CA3AF",
                              }}
                            >
                              No skills listed
                            </Typography>
                          );
                        }
                      }

                      // Handle string (comma-separated or single skill)
                      if (typeof skills === "string") {
                        const skillArray = skills
                          .split(",")
                          .map((s) => s.trim())
                          .filter((s) => s);
                        if (skillArray.length > 0) {
                          return skillArray.map((skill, index) => (
                            <Chip
                              key={index}
                              label={skill}
                              sx={{
                                backgroundColor: "#EFF6FF",
                                color: "#155DFC",
                                fontFamily: "Arial",
                                fontWeight: 400,
                                fontSize: "14px",
                                height: "32px",
                                borderRadius: "6px",
                                "& .MuiChip-label": {
                                  padding: "0 12px",
                                },
                              }}
                            />
                          ));
                        }
                      }

                      // Fallback: display as single chip
                      return (
                        <Chip
                          label={String(skills)}
                          sx={{
                            backgroundColor: "#EFF6FF",
                            color: "#155DFC",
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "14px",
                            height: "32px",
                            borderRadius: "6px",
                            "& .MuiChip-label": {
                              padding: "0 12px",
                            },
                          }}
                        />
                      );
                    })()}
                  </Box>
                </Box>

                {/* Button Container */}
                {selectedDecisionMaker?.linkedin_url && (
                  <Box
                    sx={{
                      width: "624px",
                      paddingTop: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <Button
                      onClick={() => {
                        if (selectedDecisionMaker.linkedin_url) {
                          window.open(
                            selectedDecisionMaker.linkedin_url,
                            "_blank"
                          );
                        }
                      }}
                      sx={{
                        width: "624px",
                        height: "50px",
                        borderRadius: "10px",
                        border: "1px solid #D1D5DC",
                        backgroundColor: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                      }}
                    >
                      <img
                        src="/Iconwithbutton.svg"
                        alt="LinkedIn"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Arial",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0px",
                          color: "#364153",
                          textAlign: "center",
                        }}
                      >
                        View LinkedIn
                      </Typography>
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Dialog>

        {/* Send Message Modal */}
        <Dialog
          open={messageModalOpen}
          onClose={() => {
            setMessageModalOpen(false);
            setUserPrompt("");
            setAiMessage("");
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 2,
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Send LinkedIn Message
            </Typography>
            <IconButton
              onClick={() => {
                setMessageModalOpen(false);
                setUserPrompt("");
                setAiMessage("");
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Recipients Info */}
              <Box
                sx={{
                  backgroundColor: "#F9FAFB",
                  padding: "12px",
                  borderRadius: "8px",
                }}
              >
                <Typography variant="body2" sx={{ color: "#6B7280", mb: 1 }}>
                  Sending to{" "}
                  {
                    selectedDecisionMakers.filter((dm) => dm.linkedin_url)
                      .length
                  }{" "}
                  decision maker(s)
                </Typography>
                <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                  {selectedDecisionMakers
                    .filter((dm) => dm.linkedin_url)
                    .map((dm) => dm.full_name)
                    .join(", ")}
                </Typography>
              </Box>

              {/* User Prompt Input */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Write your message prompt
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  placeholder="e.g., Write a professional LinkedIn message for a software engineer candidate with 5 years of experience in Python and React. Mention that we have a remote position with competitive salary."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#D1D5DB",
                      },
                      "&:hover fieldset": {
                        borderColor: "#9CA3AF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleGenerateMessage}
                  disabled={isGenerating || !userPrompt.trim()}
                  sx={{
                    mt: 2,
                    backgroundColor: "#3B82F6",
                    "&:hover": {
                      backgroundColor: "#2563EB",
                    },
                    textTransform: "none",
                    borderRadius: "8px",
                  }}
                >
                  {isGenerating ? "Generating..." : "Generate AI Message"}
                </Button>
              </Box>

              {/* AI Generated Message */}
              {aiMessage && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      AI Generated Message
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleRegenerateMessage}
                      disabled={isGenerating || !userPrompt.trim()}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        borderColor: "#D1D5DB",
                        color: "#6B7280",
                        "&:hover": {
                          borderColor: "#9CA3AF",
                          backgroundColor: "#F9FAFB",
                        },
                      }}
                    >
                      Regenerate
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={aiMessage}
                    onChange={(e) => setAiMessage(e.target.value)}
                    placeholder="AI generated message will appear here..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#F9FAFB",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", mt: 1, display: "block" }}
                  >
                    You can edit the message above before sending
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              padding: "16px 24px",
              borderTop: "1px solid #E5E7EB",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={() => {
                setMessageModalOpen(false);
                setUserPrompt("");
                setAiMessage("");
              }}
              sx={{ color: "#6B7280", textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendMessages}
              disabled={!aiMessage.trim() || isSending}
              sx={{
                backgroundColor: "#3B82F6",
                "&:hover": {
                  backgroundColor: "#2563EB",
                },
                "&:disabled": {
                  backgroundColor: "#93C5FD",
                },
                textTransform: "none",
                borderRadius: "8px",
              }}
            >
              {isSending ? "Sending..." : "Send Messages"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Search Form View
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Centered content wrapper (title + form card) */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: { xs: "16px", sm: "24px" },
          overflow: "auto",
          overflowX: "hidden",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "1216px",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Title Section */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                mb: 1,
                fontSize: { xs: "20px", sm: "28px", md: "32px" }
              }}
            >
              Find Companies & Decision Makers
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" } }}
            >
              Search for companies and extract decision makers for your outreach
              campaign
            </Typography>
          </Box>

          {/* Error Alert */}
          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage(null)}
              sx={{
                mb: 3,
                borderRadius: "8px",
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              {errorMessage}
            </Alert>
          )}

          {/* Form with Border Box */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "16px", sm: "24px" },
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: { xs: "16px", sm: "24px", md: "32px" },
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Batch Name */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Batch name
                <span style={{ color: "#EF4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={formData.searchName}
                onChange={handleChange("searchName")}
                placeholder="Enter a name for this search"
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "& fieldset": {
                      borderColor: "#D1D5DB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9CA3AF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3B82F6",
                    },
                  },
                }}
              />
            </Box>

            {/* AI Search Field */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Describe what you're looking for
                <span style={{ color: "#EF4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="Example: Find SaaS companies in the healthcare industry located in United States with 50-200 employees..."
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "& fieldset": {
                      borderColor: "#D1D5DB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9CA3AF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3B82F6",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "inherit",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>

            {/* Total Companies */}
            <Box>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontSize: { xs: "13px", sm: "14px" } }}>
                    Total companies
                    <span style={{ color: "#EF4444" }}>*</span>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={wantPhoneNumber}
                        onChange={(e) => setWantPhoneNumber(e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#3B82F6",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#3B82F6",
                            },
                          "& .MuiSwitch-switchBase": {
                            color: "#9CA3AF",
                          },
                          "& .MuiSwitch-track": {
                            backgroundColor: "#E5E7EB",
                          },
                        }}
                      />
                    }
                    label="Want Phone Number"
                    sx={{
                      margin: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        fontWeight: 500,
                        marginLeft: "8px",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                type="number"
                value={totalCompanies}
                onChange={(e) => setTotalCompanies(e.target.value)}
                placeholder="Enter number of companies"
                required
                inputProps={{ min: 1 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "& fieldset": {
                      borderColor: "#D1D5DB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9CA3AF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3B82F6",
                    },
                  },
                }}
              />
            </Box>

            {/* LinkedIn Search Fields - REMOVED */}
            {false && (
              <>
                {/* Search Name */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Search Name
                    <span style={{ color: "#EF4444" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    value={linkedinFormData.search_name}
                    onChange={(e) =>
                      setLinkedinFormData({
                        ...linkedinFormData,
                        search_name: e.target.value,
                      })
                    }
                    placeholder="e.g., 2st search"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{ color: "#6B7280", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      },
                    }}
                  />
                </Box>

                {/* Industry */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Industry
                    <span style={{ color: "#EF4444" }}>*</span>
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      multiple
                      value={linkedinFormData.industry}
                      onChange={(e) =>
                        setLinkedinFormData({
                          ...linkedinFormData,
                          industry: e.target.value,
                        })
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              sx={{
                                backgroundColor: "#EFF6FF",
                                color: "#1E40AF",
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      }}
                    >
                      {Object.keys(INDUSTRY_NAME_TO_ID)
                        .sort()
                        .map((industryName) => (
                          <MenuItem key={industryName} value={industryName}>
                            <Checkbox
                              checked={
                                linkedinFormData.industry.indexOf(
                                  industryName
                                ) > -1
                              }
                            />
                            <ListItemText primary={industryName} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "#6B7280" }}
                  >
                    Select one or more industries
                  </Typography>
                </Box>

                {/* Location */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Location
                    <span style={{ color: "#EF4444" }}>*</span>
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <LocationOnIcon
                      sx={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#6B7280",
                        fontSize: "20px",
                        zIndex: 1,
                        pointerEvents: "none",
                      }}
                    />
                    <FormControl fullWidth required>
                      <Select
                        multiple
                        value={getCountryNamesFromIds(
                          linkedinFormData.location
                        )}
                        onChange={(e) => {
                          const selectedCountries = e.target.value;
                          const locationIds = selectedCountries
                            .map((country) => LINKEDIN_LOCATION_IDS[country])
                            .filter((id) => id !== undefined)
                            .map((id) => id.toString());
                          setLinkedinFormData({
                            ...linkedinFormData,
                            location: locationIds,
                          });
                        }}
                        displayEmpty
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return (
                              <Typography sx={{ color: "#9CA3AF", ml: "28px" }}>
                                Select one or more countries
                              </Typography>
                            );
                          }
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                ml: "28px",
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  sx={{
                                    height: "24px",
                                    fontSize: "0.75rem",
                                  }}
                                />
                              ))}
                            </Box>
                          );
                        }}
                        sx={{
                          borderRadius: "8px",
                          backgroundColor: "#FFFFFF",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#D1D5DB",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#9CA3AF",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3B82F6",
                          },
                          "& .MuiSelect-select": {
                            paddingLeft: "40px",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                        }}
                      >
                        {Object.keys(LINKEDIN_LOCATION_IDS)
                          .sort()
                          .map((country) => (
                            <MenuItem key={country} value={country}>
                              <Checkbox
                                checked={
                                  getCountryNamesFromIds(
                                    linkedinFormData.location
                                  ).indexOf(country) > -1
                                }
                              />
                              <ListItemText primary={country} />
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "#6B7280" }}
                  >
                    Select one or more countries from the dropdown
                  </Typography>
                </Box>

                {/* Number of Companies */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Number of Companies
                    <span style={{ color: "#EF4444" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={linkedinFormData.number_of_companies}
                    onChange={(e) =>
                      setLinkedinFormData({
                        ...linkedinFormData,
                        number_of_companies: e.target.value,
                      })
                    }
                    placeholder="Enter number of companies"
                    required
                    inputProps={{ min: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{ color: "#6B7280", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      },
                    }}
                  />
                </Box>

                {/* Number of Employees */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Number of Employees
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      multiple
                      value={linkedinFormData.headcount_ranges}
                      onChange={(e) =>
                        setLinkedinFormData({
                          ...linkedinFormData,
                          headcount_ranges: e.target.value,
                        })
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={HEADCOUNT_ID_TO_TEXT[value] || value}
                              size="small"
                              sx={{
                                backgroundColor: "#EFF6FF",
                                color: "#1E40AF",
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      }}
                    >
                      {Object.entries(HEADCOUNT_ID_TO_TEXT).map(
                        ([id, text]) => (
                          <MenuItem key={id} value={id}>
                            <Checkbox
                              checked={
                                linkedinFormData.headcount_ranges.indexOf(id) >
                                -1
                              }
                            />
                            <ListItemText primary={text} />
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "#6B7280" }}
                  >
                    Select one or more headcount ranges
                  </Typography>
                </Box>
              </>
            )}

            {/* Country Selection Dialog */}
            <Dialog
              open={locationDialogOpen}
              onClose={() => {
                setLocationDialogOpen(false);
                setCountrySearchTerm("");
              }}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  maxHeight: "80vh",
                },
              }}
            >
              <DialogTitle
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #E5E7EB",
                  pb: 2,
                }}
              >
                Select Countries
                <Button
                  onClick={() => {
                    setLocationDialogOpen(false);
                    setCountrySearchTerm("");
                  }}
                  sx={{
                    minWidth: "auto",
                    padding: "4px",
                    color: "#6B7280",
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                >
                  <CloseIcon />
                </Button>
              </DialogTitle>
              <DialogContent sx={{ padding: "0 !important" }}>
                <Box
                  sx={{
                    padding: "16px",
                    borderBottom: "1px solid #E5E7EB",
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Search countries..."
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: "#6B7280", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      },
                    }}
                  />
                </Box>
                <List
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    padding: 0,
                  }}
                >
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <ListItem key={country} disablePadding>
                        <ListItemButton
                          onClick={() => handleCountrySelect(country)}
                          sx={{
                            padding: "12px 16px",
                            "&:hover": {
                              backgroundColor: "#F9FAFB",
                            },
                          }}
                        >
                          <Checkbox
                            checked={formData.location.includes(country)}
                            sx={{
                              color: "#3B82F6",
                              "&.Mui-checked": {
                                color: "#3B82F6",
                              },
                            }}
                          />
                          <ListItemText primary={country} />
                        </ListItemButton>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No countries found"
                        sx={{ textAlign: "center" }}
                        primaryTypographyProps={{ color: "text.secondary" }}
                      />
                    </ListItem>
                  )}
                </List>
              </DialogContent>
              <DialogActions
                sx={{ padding: "16px", borderTop: "1px solid #E5E7EB" }}
              >
                <Button
                  onClick={() => {
                    setLocationDialogOpen(false);
                    setCountrySearchTerm("");
                  }}
                  variant="contained"
                  sx={{
                    backgroundColor: "#3B82F6",
                    "&:hover": {
                      backgroundColor: "#2563EB",
                    },
                    textTransform: "none",
                  }}
                >
                  Done
                </Button>
              </DialogActions>
            </Dialog>

            {/* Not Location Selection Dialog */}
            <Dialog
              open={notLocationDialogOpen}
              onClose={() => {
                setNotLocationDialogOpen(false);
                setNotCountrySearchTerm("");
              }}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  maxHeight: "80vh",
                },
              }}
            >
              <DialogTitle
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #E5E7EB",
                  pb: 2,
                }}
              >
                Exclude Countries
                <Button
                  onClick={() => {
                    setNotLocationDialogOpen(false);
                    setNotCountrySearchTerm("");
                  }}
                  sx={{
                    minWidth: "auto",
                    padding: "4px",
                    color: "#6B7280",
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                >
                  <CloseIcon />
                </Button>
              </DialogTitle>
              <DialogContent sx={{ padding: "0 !important" }}>
                <Box
                  sx={{
                    padding: "16px",
                    borderBottom: "1px solid #E5E7EB",
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Search countries..."
                    value={notCountrySearchTerm}
                    onChange={(e) => setNotCountrySearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: "#6B7280", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      },
                    }}
                  />
                </Box>
                <List
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    padding: 0,
                  }}
                >
                  {countries.filter((country) =>
                    country
                      .toLowerCase()
                      .includes(notCountrySearchTerm.toLowerCase())
                  ).length > 0 ? (
                    countries
                      .filter((country) =>
                        country
                          .toLowerCase()
                          .includes(notCountrySearchTerm.toLowerCase())
                      )
                      .map((country) => (
                        <ListItem key={country} disablePadding>
                          <ListItemButton
                            onClick={() => handleNotCountrySelect(country)}
                            sx={{
                              padding: "12px 16px",
                              "&:hover": {
                                backgroundColor: "#F9FAFB",
                              },
                            }}
                          >
                            <Checkbox
                              checked={formData.notLocations.includes(country)}
                              sx={{
                                color: "#EF4444",
                                "&.Mui-checked": {
                                  color: "#EF4444",
                                },
                              }}
                            />
                            <ListItemText primary={country} />
                          </ListItemButton>
                        </ListItem>
                      ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No countries found"
                        sx={{ textAlign: "center" }}
                        primaryTypographyProps={{ color: "text.secondary" }}
                      />
                    </ListItem>
                  )}
                </List>
              </DialogContent>
              <DialogActions
                sx={{ padding: "16px", borderTop: "1px solid #E5E7EB" }}
              >
                <Button
                  onClick={() => {
                    setNotLocationDialogOpen(false);
                    setNotCountrySearchTerm("");
                  }}
                  variant="contained"
                  sx={{
                    backgroundColor: "#3B82F6",
                    "&:hover": {
                      backgroundColor: "#2563EB",
                    },
                    textTransform: "none",
                  }}
                >
                  Done
                </Button>
              </DialogActions>
            </Dialog>

            {/* Search Options Checkboxes */}
            <Box sx={{ marginTop: "8px" }}>
              <FormGroup sx={{ flexDirection: "row", gap: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchWithGoogleMaps}
                      onChange={(e) =>
                        setSearchWithGoogleMaps(e.target.checked)
                      }
                    />
                  }
                  label="Search with Google Maps"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchWithLinkedIn}
                      onChange={(e) => setSearchWithLinkedIn(e.target.checked)}
                    />
                  }
                  label="Search with LinkedIn"
                />
              </FormGroup>
            </Box>

            {/* Submit Button */}
            <Box sx={{ marginTop: "8px" }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={isLoading}
                sx={{
                  backgroundColor: isLoading ? "#93C5FD" : "primary.main",
                  color: "#FFFFFF",
                  fontWeight: 500,
                  padding: "10px 24px",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  },
                  "&:disabled": {
                    backgroundColor: "#93C5FD",
                    boxShadow: "none",
                  },
                }}
              >
                {isLoading ? "Finding companies..." : "Find Companies"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LeadGeneration;
